import React, { useMemo } from "react";
import { Popover } from "ui-components/popover/popover";
import Amount from "ui-components/molecules/amount/amount";
import { CustomCellTypes } from "ui-components/table/custom-cells/customCell";
import { PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import "../outboundPlanningList.scss";

export type HandlingUnitMetricKind = "count" | "rolls" | "nonRolled";

export interface PlanningFreightTotalsMetric {
  id: string;
  label: string;
  tooltip?: string;
  type: CustomCellTypes;
  count?: boolean;
  sumField?: keyof PlanningFreightProbill;
  handlingUnit?: HandlingUnitMetricKind;
}

export const PLANNING_HANDLING_UNIT_TOTALS_METRICS: PlanningFreightTotalsMetric[] = [
  {
    id: "handlingUnits",
    label: "Handling Units",
    tooltip: "Count of handling units on freight items",
    type: CustomCellTypes.number,
    handlingUnit: "count",
  },
  {
    id: "rolls",
    label: "Rolls",
    tooltip: "Count of rolled handling units",
    type: CustomCellTypes.number,
    handlingUnit: "rolls",
  },
  {
    id: "nonRolled",
    label: "Non-Rolled",
    tooltip: "Count of non-rolled handling units",
    type: CustomCellTypes.number,
    handlingUnit: "nonRolled",
  },
];

export const PLANNING_FREIGHT_TOTALS_METRICS: PlanningFreightTotalsMetric[] = [
  {
    id: "probillCount",
    label: "Probill",
    tooltip: "Count of all probills in the list",
    type: CustomCellTypes.number,
    count: true,
  },
  ...PLANNING_HANDLING_UNIT_TOTALS_METRICS,
  {
    id: "probillWeight",
    label: "Weight",
    tooltip: "Sum of probill weight",
    type: CustomCellTypes.number,
    sumField: "probillWeight",
  },
  {
    id: "probillPieces",
    label: "Pieces",
    tooltip: "Sum of probill pieces",
    type: CustomCellTypes.number,
    sumField: "probillPieces",
  },
  {
    id: "probillFreightCharge",
    label: "Freight Charge",
    tooltip: "Sum of probill freight charge",
    type: CustomCellTypes.amount,
    sumField: "probillFreightCharge",
  },
];

export const PLANNING_PICKUP_TOTALS_METRICS: PlanningFreightTotalsMetric[] = [
  {
    id: "pickupCount",
    label: "Pickups",
    tooltip: "Count of all pickups in the list",
    type: CustomCellTypes.number,
    count: true,
  },
  {
    id: "probillWeight",
    label: "Weight",
    tooltip: "Sum of pickup weight",
    type: CustomCellTypes.number,
    sumField: "probillWeight",
  },
  {
    id: "probillPieces",
    label: "Pieces",
    tooltip: "Sum of pickup pieces",
    type: CustomCellTypes.number,
    sumField: "probillPieces",
  },
  {
    id: "probillFreightCharge",
    label: "Freight Charge",
    tooltip: "Sum of pickup freight charge",
    type: CustomCellTypes.amount,
    sumField: "probillFreightCharge",
  },
];

interface PlanningFreightListTotalsBarProps {
  rows: PlanningFreightProbill[];
  rowHeight?: number;
  metrics?: PlanningFreightTotalsMetric[];
  /** When set, HU / roll counts use these rows instead of `rows` (e.g. exclude pickups). */
  handlingUnitRows?: PlanningFreightProbill[];
}

function formatMetricValue(
  metric: PlanningFreightTotalsMetric,
  value: unknown
): React.ReactNode {
  if (value == null || value === "") return "—";
  if (metric.type === CustomCellTypes.amount) {
    return <Amount amount={Number(value)} />;
  }
  const formatted = Number(value).toLocaleString("en", {
    useGrouping: true,
    maximumFractionDigits: 2,
  });
  if (metric.sumField === "probillWeight") {
    return `${formatted} lbs`;
  }
  return formatted;
}

function summarizeHandlingUnits(rows: PlanningFreightProbill[]) {
  let handlingUnitCount = 0;
  let rollCount = 0;

  for (const row of rows) {
    for (const item of row.items ?? []) {
      handlingUnitCount += 1;
      const isRoll = String(item.itemIsRoll ?? "").trim().toUpperCase();
      if (isRoll === "Y") rollCount += 1;
    }
  }

  return {
    handlingUnitCount,
    rollCount,
    nonRolledCount: handlingUnitCount - rollCount,
  };
}

function resolveMetricValue(
  metric: PlanningFreightTotalsMetric,
  rows: PlanningFreightProbill[],
  handlingUnitRows: PlanningFreightProbill[]
): unknown {
  if (metric.count) return rows.length;
  if (metric.sumField) {
    return rows.reduce((sum, row) => sum + (Number(row[metric.sumField!]) || 0), 0);
  }
  if (metric.handlingUnit) {
    const hu = summarizeHandlingUnits(handlingUnitRows);
    if (metric.handlingUnit === "count") return hu.handlingUnitCount;
    if (metric.handlingUnit === "rolls") return hu.rollCount;
    return hu.nonRolledCount;
  }
  return null;
}

const PlanningFreightListTotalsBar: React.FC<PlanningFreightListTotalsBarProps> = ({
  rows,
  rowHeight = 44,
  metrics = PLANNING_FREIGHT_TOTALS_METRICS,
  handlingUnitRows,
}) => {
  const resolvedMetrics = useMemo(() => {
    const huRows = handlingUnitRows ?? rows;
    return metrics.map((metric) => ({
      ...metric,
      value: resolveMetricValue(metric, rows, huRows),
    }));
  }, [metrics, rows, handlingUnitRows]);

  return (
    <div
      className="planning-freight-list-totals-bar"
      role="row"
      style={{ minHeight: rowHeight }}
    >
      <div className="planning-freight-list-totals-bar__label">Totals</div>
      <div className="planning-freight-list-totals-bar__metrics">
        {resolvedMetrics.map((metric) => (
          <div className="planning-freight-list-totals-bar__metric" key={metric.id}>
            <span className="planning-freight-list-totals-bar__metric-label">{metric.label}</span>
            {metric.tooltip ? (
              <Popover
                trigger="hover"
                values={
                  <div className="planning-freight-list-totals-bar__tooltip">{metric.tooltip}</div>
                }
              />
            ) : null}
            <span className="planning-freight-list-totals-bar__metric-value">
              {formatMetricValue(metric, metric.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanningFreightListTotalsBar;
